import type { Product, Discount } from '@/payload-types'
import { extractIdsFromRelationship } from '@/lib/discounts'

export interface ProductDiscountInfo {
  hasVariants: boolean
  hasBulkDiscount: boolean
  hasManualDiscount: boolean
  manualDiscountPercentage?: number
  bulkDiscountPercentage?: number
}

export function getProductDiscountInfo(
  product: Partial<Product>,
  activeDiscounts?: Discount[],
): ProductDiscountInfo {
  const result: ProductDiscountInfo = {
    hasVariants: false,
    hasBulkDiscount: false,
    hasManualDiscount: false,
  }

  // Check if product has variants
  // We check for enableVariants being true OR if variants array has items
  // This handles cases where enableVariants might be missing but variants exist
  if ((product.enableVariants || (product.variants && product.variants.length > 0))) {
    result.hasVariants = true
  }

  // If no active discounts, return default result
  if (!activeDiscounts || activeDiscounts.length === 0) {
    return result
  }

  // Check each active discount
  for (const discount of activeDiscounts) {
    if (!discount.is_active) continue

    // Check if discount applies to this product
    let appliesToProduct = false

    if (discount.appliesTo === 'all') {
      appliesToProduct = true
    } else if (discount.appliesTo === 'product' && discount.products) {
      const productIds = extractIdsFromRelationship(discount.products)
      appliesToProduct = productIds.includes(product.id!)
    } else if (discount.appliesTo === 'category' && discount.category) {
      const categoryIds = extractIdsFromRelationship(discount.category)
      const productCategoryIds = extractIdsFromRelationship(product.categories)
      appliesToProduct = productCategoryIds.some((id) => categoryIds.includes(id))
    }

    if (!appliesToProduct) continue

    // Check discount type and percentage
    if (discount.discountMode === 'bulk') {
      if (discount.apply_as_volume_pricing && (discount as any).volumePricing && (discount as any).volumePricing.length > 0) {
        result.hasBulkDiscount = true
        // Use the highest discount percentage from tiers
        const maxTierDiscount = Math.max(...(discount as any).volumePricing.map((t: any) => t.discount_percentage || 0))
        result.bulkDiscountPercentage = maxTierDiscount
      } else if (discount.discount_percentage) {
        result.hasBulkDiscount = true
        result.bulkDiscountPercentage = discount.discount_percentage
      }
    } else if (discount.discountMode === 'manual' && discount.discount_percentage) {
      result.hasManualDiscount = true
      result.manualDiscountPercentage = discount.discount_percentage
    }
  }

  return result
}

/**
 * Calculate the price after applying a discount percentage.
 * Rounds to 2 decimal places to avoid floating-point artifacts.
 */
export function calculateDiscountedPrice(
  originalPrice: number,
  discountPercentage: number,
): number {
  const discounted = originalPrice * (1 - discountPercentage / 100)
  return Math.round(discounted * 100) / 100
}
